import Vue from 'vue';
import _memoize from 'lodash/memoize';
import _debounce from 'lodash/debounce';
import _cloneDeep from 'lodash/cloneDeep';

/**
 * Sets an object to the vuex storage
 * @param objects the objects to add (required to have an id attribute)
 * @param storage the vuex store
 * @param default props to set if they don't exist in object
 */
export function setObjects(objects, storage, defaults, identifier) {

    defaults = defaults || {}
    identifier = identifier || 'id'

    for ( var obj of objects ) {

        if ( storage[obj[identifier]] != undefined ) {
            // don't replace, update all properties
            // otherwise this object is considered a totally new object
            // note that defaults are not set here, they should not overwrite already set values
            for ( const prop in obj ) {
                storage[obj[identifier]][prop] = obj[prop];
            }

        } else {
            Vue.set(storage, obj[identifier], Object.assign({}, _cloneDeep(defaults), obj));
        }

    }

}

/**
 * Reactively deletes the given id from the storage
 * @param  id
 * @param  storage
 */
export function removeObject(id, storage) {
    Vue.delete(storage, id)
}


/**
 * Transforms JSON API response to vuex storage objects
 * => {
 *       { module_name }: [
 *          { data }
 *        ]
 *    }

 * @param  {JSON API RESPONSE OBJECT} response
 * @return {Flattened vuex model}
 */
export function transform(response) {

    let result = {}
    let data = response.data instanceof Array ? response.data : [response.data]
    let resources = data.concat(response.included || [])

    for( let res of resources ) {

        let data = res.attributes
        data.id = res.id

        for( let relation in res.relationships ) {

            let rel = res.relationships[relation].data

            if ( rel instanceof Array ) {
                data[relation] = rel.map( r => r.id )
            } else if ( rel == null ) {
                data[relation] = null
            } else {
                data[relation] = [rel.id] // TODO: remove array here?
            }

        }

        result[res.type] = result[res.type] || []
        result[res.type].push(data)

    }

    return result
}


/**
 * Memoizes `debounceSync` for multiple arguments, resolved with given resolver
 *
 * @param  {Function} func      function that returns a promise
 * @param  {Function} resolver  function that is used to resolve a memoized `debounceSync`
 * @param  {Int} delay          debounce delay
 * @param  {Object} opts        debounce options
 * @return {Function}           memoized debounced function that returns a promise
 */
export function debounceSyncMem( func, resolver, delay, opts ) {

    const memoize = _memoize(
        () => debounceSync( func, delay, opts ),
        resolver
    )

    function sync(...args) {
        // first apply to resolve, second to call debounceSync
        return memoize.apply(this, args).apply(this, args)
    }

    // proxy flush and cancel
    sync.flush = (...args) => memoize.apply(this, args).flush()
    sync.cancel = (...args) => memoize.apply(this, args).cancel()
    sync.reset = (...args) => memoize.apply(this, args).reset()
    sync.retry = (...args) => memoize.apply(this, args).retry()

    return sync

}

/**
 * Debounces the given function, but makes sure it waits for `func` to be
 * completed before calling next `func`
 *
 * @param  {Function} func  function that returns a promise
 * @param  {Int} delay      debounce delay
 * @param  {Object} opts    debounce options
 * @return {Function}       debounced function that returns a promise
 */
export function debounceSync( func, delay, opts ) {

    let previousSync = Promise.resolve()
    let lastCall = Promise.resolve()
    let lastArgs = null
    let scheduledFlush = null

    const debounced = _debounce( function(resolve, reject, ...args) {

        // chain sync after previous
        return previousSync = previousSync
            .then( () => func.apply(this, args) )
            .then( resolve, reject ) // proxy to given resolve + reject functions

    }, delay, opts)

    // returned promise only gets resolved/rejected when the debounced function is called
    function debouncedWrap(...args) {

        lastArgs = args.slice() // copy arguments for `retry`

        return lastCall = new Promise( (resolve, reject) => {

            args.unshift(resolve, reject) // prepend
            debounced.apply(this, args)

        })

    }

    // flushes as soon as previous sync has finished
    debouncedWrap.flush = function() {

        // can only schedule one flush
        if ( scheduledFlush ) return scheduledFlush;

        return scheduledFlush = previousSync
            .then( debounced.flush )
            .then( () => scheduledFlush = null ) // cleanup
            .then( () => lastCall ) // return lastCall promise which proxies resolve/reject

    }

    debouncedWrap.retry = () => debouncedWrap.apply( this, lastArgs )
    debouncedWrap.cancel = debounced.cancel
    debouncedWrap.reset = () => previousSync = Promise.resolve()

    return debouncedWrap

}



/**
 * Generates GUID
 * @return GUID
 */
export function guid() {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
}

/**
 * Generates current UNIX timestamp
 * @return {Int} the current timestamp
 */
export function timestamp() {
    return Math.floor(Date.now() / 1000)
}


export function logreject(reason, error, ...args) {
    console.error(reason, error, args)
    return Promise.reject(error)
}



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/helpers.js